import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BellRing, CalendarDays, ChevronLeft, ChevronRight, List, Plus } from "lucide-react";
import { useFacility } from "../../context/FacilityContext";
import { ConfirmDialog, EmptyRow, FilterSelect, SearchBox, UndoToast } from "../components/crm/ui";
import { Pagination, apiGet, usePagination } from "../components/crm/contactsKit";
import { PrimaryButton } from "../components/ui";
import { InlineLoader, PageLoader } from "../components/PageLoader";
import { DAY } from "../lib/format";
import { AppointmentListHeader, AppointmentRow, AppointmentViewDrawer } from "../components/appointments/AppointmentRow";
import { AppointmentDrawer } from "../components/appointments/AppointmentDrawer";
import { MonthCalendar, TimeCalendar, compactTime, dateLabel, dayStart, monday } from "../components/appointments/Calendars";
import { APPOINTMENT_STATUSES, STATUS_KEY, apiSend, breakOn, loadAppointments, loadAppointmentsChecked, loadDoctors, loadFacilityHours, loadReminderSettings, toAppt } from "../components/appointments/appointmentsData";
import { ReminderSettingsDrawer } from "../components/appointments/ReminderSettings";
import { AccessNotice, fetchApi, reasonOf } from "../components/marketing/access";
import { EhrPushButton } from "../components/patients/EhrPushButton";
import { ViewOnlyNote, useModuleAccess, viewOnlyTitle } from "../components/patients/clinicalAccess";

/**
 * Appointments (design: screens/Appointments.tsx, components/appointments/*).
 *
 * Routes: /appointments is the screen; /appointments/new opens it with the
 * New appointment drawer open; /appointments/:id opens it with that
 * appointment's view drawer open. Closing either drawer goes back to
 * /appointments.
 *
 * Real data and actions:
 *   list and calendar ......... GET /api/appointments?date_from&date_to (&facility_id)
 *   header counts ............. this week's appointments (same endpoint)
 *   empty state ............... GET /api/appointments/stats (total)
 *   one appointment by link ... GET /api/appointments/:id
 *   other visits .............. GET /api/appointments?patient_id
 *   doctors, filters, chips ... GET /api/staff?role=doctor (&facility_id)
 *   locations ................. the location switcher (FacilityContext)
 *   lunch band ................ the location's break, GET /api/facility-schedules
 *   status, check in, start consultation (in_progress), complete, no-show ... PUT /api/appointments/:id { status }
 *   cancel, with a reason ..... POST /api/appointments/:id/cancel { cancellation_reason }
 *   undo a status change ...... PUT /api/appointments/:id { status: previous }
 *   new, edit, reschedule ..... see components/appointments/AppointmentDrawer.js
 *   delete, with Undo ......... DELETE /api/appointments/:id, POST …/:id/restore (item 171)
 *   WhatsApp reminder now ..... POST /api/appointments/:id/send-reminder (172)
 *   WhatsApp confirmation ..... POST /api/appointments/:id/send-confirmation (view drawer; the clinic's
 *                               appointment_confirmation template, else appointment_reminder; not the row's reminder)
 *   reminder sent / read ...... each row's reminder { status, sent_at } (173)
 *   chair, linked lead ........ chair_id / lead_id on the row (174, 176)
 *   book from a lead or patient  /appointments/new?lead_id= or ?patient_id=
 *   unknown /appointments/:id .. "Appointment not found" (404) or the load error, over the list
 *   automatic reminders ....... GET / PUT /api/appointments/reminder-settings (Reminders; PUT is owner / admin only)
 *   push to EHR ............... GET /api/sync-keys/sync-status, POST /api/sync-keys/push { entity: appointments }
 *   view only / no access ..... staff permissions on Appointments (components/patients/clinicalAccess)
 * Ask Olli has no appointment prefill (its only form prefill is the lead), so
 * the drawer has no "Filled by Olli" state.
 */

const RANGES = ["Today", "This week", "Upcoming", "Past"];
const MODES = ["day", "week", "month"];

export default function Appointments() {
  const fac = useFacility();
  const facilityId = fac.apiParams().facility_id || null;
  const facilityLabel = fac.currentFacility?.name || "All locations";
  // chairs: the location's chair count (null when not set), for the chair picker (item 174).
  const facilities = useMemo(() => (fac.facilities || []).map((f) => ({ id: f.id, name: f.name, chairs: f.chairs ?? null })), [fac.facilities]);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [view, setView] = useState("list");
  const [mode, setMode] = useState("week");
  const [date, setDate] = useState(dayStart(new Date()));
  const [range, setRange] = useState("Today");
  const [doctor, setDoctor] = useState("");
  const [facility, setFacility] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [weekItems, setWeekItems] = useState([]);
  const [extra, setExtra] = useState({});
  const [total, setTotal] = useState(null);
  // The range the list on screen was loaded for (and the week the header counts
  // were loaded for). Until the answer for the current range arrives, show the
  // loader rather than the previous range's rows. A reload on the same range
  // (after a save or status change) keeps the list on screen.
  const [loadedKey, setLoadedKey] = useState(null);
  const [weekKey, setWeekKey] = useState(null);
  const [version, setVersion] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [facilityHours, setFacilityHours] = useState({});
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [prefill, setPrefill] = useState();
  const [prefillDoctor, setPrefillDoctor] = useState();
  const [confirm, setConfirm] = useState(null);
  const [undo, setUndo] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [history, setHistory] = useState(null);
  const [drawerSeed, setDrawerSeed] = useState(0);
  const [sending, setSending] = useState({});
  const [confirmations, setConfirmations] = useState({}); // appointment id → { sending, sentAt, error } (this session)
  // A refused or failed list load (fetchApi result), shown instead of an empty day.
  const [listFailure, setListFailure] = useState(null);
  // /appointments/:id that didn't load: { id, notFound } or { id, failure }.
  const [routeMiss, setRouteMiss] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  // Automatic reminders: undefined while loading, else { reminders, failure }.
  const [reminderState, setReminderState] = useState(undefined);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const access = useModuleAccess("Appointments");
  const reload = () => setVersion((v) => v + 1);

  const now = Date.now();
  const todayStart = dayStart(new Date(now));
  const weekStart = monday(todayStart);
  const todayItems = weekItems.filter((a) => a.start >= todayStart && a.start < todayStart + DAY);
  const remaining = todayItems.filter((a) => a.start >= now && a.status !== "Cancelled" && a.status !== "No-show").length;
  const noShows = weekItems.filter((a) => a.status === "No-show").length;
  const startOfWeek = monday(date);
  const listDay = dayStart(new Date(date));
  const listWeek = monday(date);
  const visibleStart = view === "list" ? (range === "Today" ? listDay : range === "This week" ? listWeek : range === "Upcoming" ? listDay + DAY : 0) : mode === "week" ? startOfWeek : mode === "day" ? dayStart(new Date(date)) : new Date(new Date(date).getFullYear(), new Date(date).getMonth(), 1).getTime();
  const visibleEnd = view === "list" ? (range === "Today" ? listDay + DAY : range === "This week" ? listWeek + 7 * DAY : range === "Upcoming" ? Infinity : listDay) : mode === "week" ? startOfWeek + 7 * DAY : mode === "day" ? dayStart(new Date(date)) + DAY : new Date(new Date(date).getFullYear(), new Date(date).getMonth() + 1, 1).getTime();

  const order = view === "list" && range === "Past" ? "DESC" : "ASC";
  const listKey = JSON.stringify({ visibleStart, visibleEnd: Number.isFinite(visibleEnd) ? visibleEnd : "inf", facilityId, order });
  const loading = loadedKey !== listKey;
  const currentWeekKey = JSON.stringify({ weekStart, facilityId });
  const weekLoading = weekKey !== currentWeekKey;
  // The visible range, from the backend.
  useEffect(() => {
    let live = true;
    const key = listKey;
    loadAppointmentsChecked({ from: visibleStart > 0 ? visibleStart : null, to: Number.isFinite(visibleEnd) ? visibleEnd - DAY : null, facilityId, order }).then(({ rows, failure }) => {
      if (!live) return;
      setItems(rows || []);
      setListFailure(failure || null);
      setLoadedKey(key);
    });
    return () => { live = false; };
  }, [visibleStart, visibleEnd, facilityId, view, range, version]); // eslint-disable-line react-hooks/exhaustive-deps
  // This week, for the header counts.
  useEffect(() => {
    let live = true;
    const key = currentWeekKey;
    loadAppointments({ from: weekStart, to: weekStart + 6 * DAY, facilityId }).then((list) => { if (live) { setWeekItems(list || []); setWeekKey(key); } });
    apiGet("/api/appointments/stats", facilityId ? { facility_id: facilityId } : {}).then((r) => live && setTotal(r?.success ? Number(r.data?.total || 0) : null));
    return () => { live = false; };
  }, [weekStart, facilityId, version]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let live = true;
    loadDoctors(facilityId).then((list) => live && setDoctors(list));
    return () => { live = false; };
  }, [facilityId]);
  useEffect(() => { loadFacilityHours().then(setFacilityHours); }, []);
  const loadReminders = useCallback(() => { loadReminderSettings().then((r) => setReminderState({ reminders: r.ok ? r.reminders : null, failure: r.ok ? null : r.failure })); }, []);
  useEffect(() => { loadReminders(); }, [loadReminders]);

  // Doctors on the roster plus anyone with an appointment in view.
  const doctorList = useMemo(() => {
    const map = new Map(doctors.map((d) => [d.id, d]));
    [...items, ...weekItems].forEach((a) => { if (a.doctorId && !map.has(a.doctorId)) map.set(a.doctorId, { id: a.doctorId, name: a.doctor }); });
    return [...map.values()];
  }, [doctors, items, weekItems]);
  const doctorNames = doctorList.map((d) => d.name);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    return items.filter((a) => {
      if (a.start < visibleStart || a.start >= visibleEnd) return false;
      if (doctor && a.doctor !== doctor) return false;
      if (facility && a.facility !== facility) return false;
      if (statusFilter && a.status !== statusFilter) return false;
      return !q || [a.code, a.patient, a.patientPhone, a.service, a.doctor].join(" ").toLowerCase().includes(q) || (digits.length >= 3 && String(a.patientPhone).replace(/\D/g, "").includes(digits));
    });
  }, [items, visibleStart, visibleEnd, doctor, facility, statusFilter, query]);

  const findAppt = useCallback((id) => items.find((x) => String(x.id) === String(id)) || weekItems.find((x) => String(x.id) === String(id)) || extra[id] || null, [items, weekItems, extra]);
  const viewing = viewingId ? findAppt(viewingId) : null;

  // /appointments/new and /appointments/:id open the matching drawer.
  const isNewRoute = /\/appointments\/new\/?$/.test(location.pathname);
  // Booked from a lead or a patient: /appointments/new?lead_id= or ?patient_id= (item 176).
  const search = new URLSearchParams(location.search);
  const routeLeadId = isNewRoute ? search.get("lead_id") || location.state?.leadId || null : null;
  const routePatientId = isNewRoute ? search.get("patient_id") || null : null;
  const routeId = !isNewRoute ? params.id : undefined;
  useEffect(() => {
    if (isNewRoute) { setSelected(null); setPrefill(undefined); setPrefillDoctor(undefined); setDrawerSeed((x) => x + 1); setNewOpen(true); }
  }, [isNewRoute]);
  useEffect(() => {
    if (!routeId) return undefined;
    setViewingId(routeId);
    setRouteMiss(null);
    let live = true;
    fetchApi(`/api/appointments/${routeId}`).then((r) => {
      if (!live) return;
      if (r.ok && r.data?.data) setExtra((m) => ({ ...m, [routeId]: toAppt(r.data.data) }));
      else setRouteMiss(r.status === 404 || r.status === 400 ? { id: routeId, notFound: true } : { id: routeId, failure: r });
    });
    return () => { live = false; };
  }, [routeId]);
  const leaveRoute = () => { if (isNewRoute || routeId) navigate("/appointments", { replace: true }); };

  // Other visits of the patient being viewed.
  const viewingPatient = viewing?.patientId;
  const viewingKey = viewing?.id;
  // A reload (after a status change) keeps the list; a different appointment shows the loader.
  useEffect(() => { setHistory(null); }, [viewingPatient, viewingKey]);
  useEffect(() => {
    if (!viewingPatient) return undefined;
    let live = true;
    loadAppointments({ patientId: viewingPatient, order: "DESC", maxPages: 1 }).then((list) => live && setHistory((list || []).filter((x) => x.id !== viewingKey)));
    return () => { live = false; };
  }, [viewingPatient, viewingKey, version]);

  const setStatus = async (a, next, reason = "") => {
    const r = next === "Cancelled" ? await apiSend("post", `/api/appointments/${a.id}/cancel`, reason.trim() ? { cancellation_reason: reason.trim() } : {}) : await apiSend("put", `/api/appointments/${a.id}`, { status: STATUS_KEY[next] });
    reload();
    return r;
  };
  const rowHandlers = {
    onView: (a) => setViewingId(a.id),
    onEdit: (a) => { setViewingId(null); launchEdit(a); },
    // Soft delete; Undo restores it (item 171). A restore that now clashes comes back as a 409 message.
    onDelete: async (a) => {
      const r = await apiSend("delete", `/api/appointments/${a.id}`);
      if (!r.ok) { setUndo({ message: r.message }); return; }
      if (String(viewingId) === String(a.id)) closeView();
      reload();
      setUndo({ message: "Appointment deleted", onUndo: () => { apiSend("post", `/api/appointments/${a.id}/restore`, {}).then((x) => { reload(); if (!x.ok) setUndo({ message: x.message }); }); } });
    },
    onStatus: async (a, next) => {
      if (next === "Cancelled") { setCancelReason(""); setConfirm(a); return; }
      const prev = a.rawStatus;
      const r = await setStatus(a, next);
      if (!r.ok) { setUndo({ message: r.message }); return; }
      setUndo({ message: `Status changed to ${next.toLowerCase()}`, onUndo: () => { apiSend("put", `/api/appointments/${a.id}`, { status: prev }).then(reload); } });
    },
    // The clinic's appointment_reminder template, now (item 172); the row then shows its delivery (173).
    onReminder: async (a) => {
      if (sending[a.id]) return;
      setSending((m) => ({ ...m, [a.id]: true }));
      const r = await apiSend("post", `/api/appointments/${a.id}/send-reminder`, {});
      setSending((m) => { const next = { ...m }; delete next[a.id]; return next; });
      setUndo({ message: r.ok ? `Reminder sent to ${a.patient || "the patient"} on WhatsApp` : r.message });
      if (r.ok) { reload(); if (extra[a.id]) apiGet(`/api/appointments/${a.id}`).then((x) => x?.success && x.data && setExtra((m) => ({ ...m, [a.id]: toAppt(x.data) }))); }
    },
    isSending: (a) => !!sending[a.id],
    // View-only staff can't send (POST): the drawer hides the button.
    onConfirmation: !access.canEdit ? undefined : async (a) => {
      if (confirmations[a.id]?.sending) return;
      const before = confirmations[a.id];
      setConfirmations((m) => ({ ...m, [a.id]: { sending: true, sentAt: before?.sentAt } }));
      const r = await apiSend("post", `/api/appointments/${a.id}/send-confirmation`, {});
      // A refusal (no confirmation template, WhatsApp not connected, wallet) shows under the button in the drawer.
      setConfirmations((m) => ({ ...m, [a.id]: r.ok ? { sentAt: r.body?.data?.sent_at || new Date().toISOString() } : { sentAt: before?.sentAt, error: r.message || "Couldn't send the confirmation." } }));
      if (r.ok) setUndo({ message: `Confirmation sent to ${a.patient || "the patient"} on WhatsApp` });
    },
    confirmation: (a) => confirmations[a.id] || {},
  };
  const openNew = (at, doctorId) => { setSelected(null); setPrefill(at); setPrefillDoctor(doctorId); setDrawerSeed((x) => x + 1); setNewOpen(true); };
  const closeDrawer = () => { setSelected(null); setNewOpen(false); setPrefill(undefined); setPrefillDoctor(undefined); leaveRoute(); };
  const closeView = () => { setViewingId(null); leaveRoute(); };
  const moveDate = (direction) => setDate((current) => {
    const next = new Date(current);
    next.setDate(next.getDate() + direction * (view === "calendar" ? mode === "week" ? 7 : mode === "month" ? 30 : 1 : range === "This week" ? 7 : 1));
    return dayStart(next);
  });
  const labelDate = view === "list" ? dateLabel(date, { weekday: "short", day: "numeric", month: "short" }) : mode === "week" ? `${dateLabel(startOfWeek, { day: "numeric" })} to ${dateLabel(startOfWeek + 6 * DAY, { day: "numeric", month: "long", year: "numeric" })}` : mode === "month" ? dateLabel(date, { month: "long", year: "numeric" }) : dateLabel(date, { weekday: "short", day: "numeric", month: "short" });

  const launchEdit = (item) => { setSelected(item); setNewOpen(false); setDrawerSeed((x) => x + 1); };
  const activeDrawer = newOpen || !!selected;
  const drawerKey = selected?.id ?? `new-${drawerSeed}`;
  const sortedAppointments = useMemo(() => [...filtered].sort((a, b) => a.start - b.start), [filtered]);
  const listRef = useRef(null);
  const pg = usePagination(sortedAppointments.length, JSON.stringify({ range, date, doctor, facility, statusFilter, query }));
  const pageAppointments = sortedAppointments.slice(pg.start, pg.end);
  const appointmentGroups = useMemo(() => {
    const groups = new Map();
    pageAppointments.forEach((a) => { const key = dayStart(new Date(a.start)); groups.set(key, [...(groups.get(key) ?? []), a]); });
    return [...groups.entries()];
  }, [pageAppointments]);
  const breakFor = (day) => breakOn(facilityHours, facilityId, day);

  return <main className="mx-auto flex max-w-[1500px] flex-col gap-5 px-8 py-8 animate-fade-in">
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div><h1 className="text-h1 font-semibold">Appointments</h1><p className="mt-1 text-small text-muted">{listFailure ? facilityLabel : <>Today {weekLoading ? <InlineLoader size={14} /> : todayItems.length} · {weekLoading ? <InlineLoader size={14} /> : remaining} left · {weekLoading ? <InlineLoader size={14} /> : noShows} no-shows this week · {facilityLabel}</>}</p></div>
      <div className="flex flex-wrap items-center justify-end gap-3">
        <EhrPushButton entity="appointments" canEdit={access.canEdit} />
        <button type="button" onClick={() => setRemindersOpen(true)} className="flex h-11 items-center gap-2 rounded-full px-4 text-small font-semibold ring-1 ring-line hover:bg-mist"><BellRing size={16} strokeWidth={1.75} />Reminders</button>
        <div className="flex h-11 rounded-full bg-mist p-1" role="group" aria-label="Appointment view">
          <button onClick={() => setView("list")} aria-pressed={view === "list"} className={`flex items-center gap-2 rounded-full px-4 text-small font-semibold ${view === "list" ? "bg-paper text-ink shadow-soft" : "text-muted hover:text-ink"}`}><List size={16} strokeWidth={1.75} />List</button>
          <button onClick={() => setView("calendar")} aria-pressed={view === "calendar"} className={`flex items-center gap-2 rounded-full px-4 text-small font-semibold ${view === "calendar" ? "bg-paper text-ink shadow-soft" : "text-muted hover:text-ink"}`}><CalendarDays size={16} strokeWidth={1.75} />Calendar</button>
        </div>
        <PrimaryButton onClick={() => openNew()} disabled={!access.canEdit} title={!access.canEdit ? viewOnlyTitle("Appointments") : undefined}><Plus size={17} strokeWidth={1.75} />New appointment</PrimaryButton>
      </div>
    </header>
    {access.viewOnly && <ViewOnlyNote module="appointments" />}
    {routeMiss && String(routeMiss.id) === String(routeId) && (routeMiss.notFound
      ? <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-warning-soft px-5 py-3.5 text-small text-warning"><span><strong>Appointment not found.</strong> Appointment #{routeMiss.id} doesn't exist or was deleted. Here is the list instead.</span><button type="button" onClick={() => navigate("/appointments", { replace: true })} className="shrink-0 font-semibold underline">Dismiss</button></div>
      : <AccessNotice reason={reasonOf(routeMiss.failure)} onRetry={() => { const id = routeId; setRouteMiss(null); fetchApi(`/api/appointments/${id}`).then((r) => { if (r.ok && r.data?.data) setExtra((m) => ({ ...m, [id]: toAppt(r.data.data) })); else setRouteMiss(r.status === 404 ? { id, notFound: true } : { id, failure: r }); }); }} compact />)}
    <div className="relative z-20 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex h-10 items-center rounded-full bg-paper ring-1 ring-line">
          <button aria-label="Previous date range" onClick={() => moveDate(-1)} className="flex h-10 w-10 items-center justify-center rounded-l-full hover:bg-mist"><ChevronLeft size={17} /></button>
          <button onClick={() => setDate(todayStart)} className="h-10 px-2 text-small font-semibold hover:text-sky-deep">Today</button>
          <button aria-label="Next date range" onClick={() => moveDate(1)} className="flex h-10 w-10 items-center justify-center rounded-r-full hover:bg-mist"><ChevronRight size={17} /></button>
        </div>
        <span className="min-w-32 text-small font-semibold tabular-nums">{labelDate}</span>
        {view === "calendar" && <div className="flex h-10 rounded-full bg-paper p-1 ring-1 ring-line" role="group" aria-label="Calendar mode">{MODES.map((item) => <button key={item} onClick={() => setMode(item)} className={`rounded-full px-3 text-caption font-semibold capitalize ${mode === item ? "bg-ink text-paper" : "text-muted hover:text-ink"}`}>{item}</button>)}</div>}
        {view === "list" && <div className="flex flex-wrap gap-1.5">{RANGES.map((item) => <button key={item} onClick={() => setRange(item)} className={`h-9 rounded-full px-3.5 text-caption font-semibold ${range === item ? "bg-ink text-paper" : "bg-paper text-muted ring-1 ring-line hover:text-ink"}`}>{item}</button>)}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2.5"><FilterSelect label="Doctor" value={doctor} options={doctorNames} onChange={setDoctor} allLabel="All doctors" />{!fac.currentFacility && <FilterSelect label="Facility" value={facility} options={facilities.map((f) => f.name)} onChange={setFacility} allLabel="All facilities" />}<FilterSelect label="Status" value={statusFilter} options={APPOINTMENT_STATUSES} onChange={setStatusFilter} allLabel="All statuses" /><SearchBox value={query} onChange={setQuery} placeholder="Patient, phone or appointment no." className="w-56" /></div>
    </div>
    {loading ? <PageLoader label="Loading appointments…" /> : listFailure ? <AccessNotice reason={reasonOf(listFailure)} onRetry={reload} /> : view === "list" ? <section className="animate-fade-in" aria-label="Appointment list">
        {total === 0 ? (
          <EmptyRow
            title="No appointments yet"
            body="Appointments will appear here when patients book with your clinic."
            action={access.canEdit ? <PrimaryButton onClick={() => openNew()}><Plus size={17} />New appointment</PrimaryButton> : undefined}
          />
        ) : filtered.length === 0 ? (
          <div className="rounded-lg bg-paper px-8 py-16 text-center shadow-soft">
            <p className="text-body font-semibold">Nothing booked for this day</p>
            <p className="mt-1 text-small text-muted">Try another date or clear the filters.</p>
            <button onClick={() => { setDoctor(""); setFacility(""); setStatusFilter(""); setQuery(""); }} className="mt-4 rounded-full px-4 py-2 text-small font-semibold ring-1 ring-line hover:bg-mist">Clear filters</button>
          </div>
        ) : (
          <>
            <div ref={listRef} className="space-y-5">
              {appointmentGroups.map(([day, dayItems]) => (
                <section key={day} className="overflow-hidden rounded-lg bg-paper shadow-soft">
                  <header className="sticky top-0 z-10 flex h-12 items-center justify-between border-b border-line bg-paper/95 px-5 backdrop-blur">
                    <h2 className="text-small font-semibold">{day === todayStart ? "Today · " : ""}{dateLabel(day, { weekday: "short", day: "numeric", month: "short" })} · {dayItems.length} appointments</h2>
                    <span className="text-caption text-muted">{compactTime(dayItems[0].start)} to {compactTime(dayItems[dayItems.length - 1].start + dayItems[dayItems.length - 1].duration * 60_000)}</span>
                  </header>
                  <div className="overflow-x-auto">
                    <div className="min-w-[1040px] divide-y divide-line">
                      <AppointmentListHeader />
                      {dayItems.map((a, i) => <AppointmentRow key={a.id} appointment={a} index={i} handlers={rowHandlers} />)}
                    </div>
                  </div>
                </section>
              ))}
            </div>
            <Pagination state={pg} noun="appointment" scrollTo={listRef} />
          </>
        )}
    </section> : <section className="animate-fade-in" aria-label="Appointment calendar">{mode === "month" ? <MonthCalendar date={date} items={filtered} doctors={doctorList} onOpen={(a) => setViewingId(a.id)} onDay={(d) => { setDate(d); setMode("day"); }} onNew={openNew} /> : <TimeCalendar mode={mode} date={date} items={filtered} doctors={doctorList} breakFor={breakFor} onOpen={(a) => setViewingId(a.id)} onNew={openNew} />}</section>}
    {activeDrawer && <AppointmentDrawer key={drawerKey} open={activeDrawer} appointment={selected} prefill={prefill} prefillDoctor={prefillDoctor} defaultFacilityId={facilityId} doctors={doctorList} facilities={facilities} facilityHours={facilityHours} reminders={reminderState === undefined ? undefined : reminderState.reminders} onOpenReminders={() => setRemindersOpen(true)} leadId={!selected ? routeLeadId : null} patientId={!selected ? routePatientId : null} onClose={closeDrawer} onSaved={(message) => { closeDrawer(); reload(); setUndo({ message }); }} onCancelAppointment={selected ? () => { setCancelReason(""); setConfirm(selected); closeDrawer(); } : undefined} />}
    <AppointmentViewDrawer
      open={!!viewing}
      appointment={viewing}
      history={history}
      onClose={closeView}
      handlers={rowHandlers}
    />
    <ReminderSettingsDrawer open={remindersOpen} reminders={reminderState?.reminders ?? null} failure={reminderState?.failure || null} onRetry={loadReminders} canEdit={!!fac.role?.is_owner_or_admin} onClose={() => setRemindersOpen(false)} onSaved={(list) => { setReminderState({ reminders: list, failure: null }); setRemindersOpen(false); setUndo({ message: list.some((x) => x.is_active) ? "Reminders saved" : "Automatic reminders are off" }); }} />
    {confirm && <ConfirmDialog title="Cancel this appointment?" body={<div className="flex flex-col gap-3"><p>{`${confirm.patient ? `${confirm.patient}'s` : "This patient's"} appointment on ${dateLabel(confirm.start, { day: "numeric", month: "short" })} at ${compactTime(confirm.start)} will be cancelled.`}</p><label className="flex flex-col gap-1.5 text-caption font-semibold text-ink">Reason <span className="sr-only">(optional)</span><textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} maxLength={500} placeholder="Optional. For example, patient is travelling" className="w-full resize-none rounded-sm border border-line bg-paper px-3 py-2 text-small font-normal text-ink outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" /></label></div>} confirmLabel="Cancel appointment" onCancel={() => setConfirm(null)} onConfirm={async () => { const target = confirm; const reason = cancelReason; setConfirm(null); const r = await setStatus(target, "Cancelled", reason); setUndo(r.ok ? { message: "Appointment cancelled", onUndo: () => { apiSend("put", `/api/appointments/${target.id}`, { status: target.rawStatus }).then(reload); } } : { message: r.message }); }} />}
    {undo && <UndoToast message={undo.message} onUndo={undo.onUndo} onDone={() => setUndo(null)} />}
  </main>;
}
